import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FactoryNotesModal = ({ open, product, onClose, onSent }) => {
  const [factoryEmail, setFactoryEmail] = useState(
    () => localStorage.getItem("raw_last_factory_email") || ""
  );
  const [factoryName, setFactoryName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!open || !product) return null;

  const history = product.email_history || [];

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/products/${product.id}/export/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RAW-${product.id.slice(0, 8)}-techpack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async () => {
    if (!factoryEmail.trim()) {
      toast.error("Enter a factory email address");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/products/${product.id}/email-tech-pack`, {
        factory_email: factoryEmail.trim(),
        factory_name: factoryName.trim(),
        message: message.trim(),
      });
      localStorage.setItem("raw_last_factory_email", factoryEmail.trim());
      toast.success(`Tech-pack sent to ${r.data.sent_to}`);
      setMessage("");
      if (onSent) onSent();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = "Send failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) msg = `Invalid: ${raw[0].msg}`;
      else if (e?.message) msg = e.message;
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="factory-notes-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#050505] border border-[#d4af37]/40 w-full max-w-3xl my-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#d4af37]/30 px-4 sm:px-6 py-4">
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37]">// FACTORY_NOTES</div>
            <div className="font-display text-xl sm:text-2xl mt-1">{product.name}</div>
            <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
              ID {product.id.slice(0, 12)} · {product.clothing_type || "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="fn-close"
            className="text-zinc-500 hover:text-[#d4af37] text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Images preview */}
        {(product.image_base64 || product.image_back || product.image_side) && (
          <div className="px-4 sm:px-6 pt-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                ["FRONT", product.image_base64],
                ["BACK", product.image_back],
                ["SIDE", product.image_side],
              ].map(([label, img]) => (
                <div
                  key={label}
                  className="aspect-square bg-[#0a0a0a] border border-zinc-800 overflow-hidden relative"
                >
                  {img ? (
                    <img
                      src={img.startsWith("data:") ? img : `data:image/png;base64,${img}`}
                      alt={label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-zinc-700 text-[9px] tracking-[0.2em]">
                      NOT GENERATED
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 text-[8px] tracking-[0.25em] font-mono text-[#d4af37] bg-black/70 px-1.5 py-0.5">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Factory notes body */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">
            // SAVED FACTORY NOTES
          </div>
          <div
            data-testid="fn-body"
            className="bg-[#0a0a0a] border border-zinc-800 p-3 sm:p-4 max-h-[320px] overflow-y-auto font-mono text-[11px] sm:text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed"
          >
            {product.factory_notes ||
              "No factory notes saved for this design. Open EDIT to generate or write them."}
          </div>
        </div>

        {/* Email-to-factory block */}
        <div className="px-4 sm:px-6 pt-6 pb-4 border-t border-zinc-900 mt-4">
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">
            // SEND TECH-PACK TO FACTORY
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input
              data-testid="fn-factory-email"
              type="email"
              placeholder="factory@manufacturer.com"
              value={factoryEmail}
              onChange={(e) => setFactoryEmail(e.target.value)}
              className="bg-[#050505] border border-zinc-800 focus:border-[#d4af37] outline-none px-3 py-2 text-sm font-mono text-white placeholder:text-zinc-600"
            />
            <input
              data-testid="fn-factory-name"
              type="text"
              placeholder="Factory / Contact name (optional)"
              value={factoryName}
              onChange={(e) => setFactoryName(e.target.value)}
              className="bg-[#050505] border border-zinc-800 focus:border-[#d4af37] outline-none px-3 py-2 text-sm font-mono text-white placeholder:text-zinc-600"
            />
          </div>
          <textarea
            data-testid="fn-message"
            placeholder="Optional note to the factory (e.g. 'Please quote for 100 units, lead time and sample cost')"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full bg-[#050505] border border-zinc-800 focus:border-[#d4af37] outline-none px-3 py-2 text-sm font-mono text-white placeholder:text-zinc-600 resize-none"
          />
          <div className="flex gap-2 mt-3 flex-wrap">
            <button
              data-testid="fn-send-btn"
              onClick={sendEmail}
              disabled={sending}
              className="btn-raw btn-raw-primary flex-1 min-w-[140px]"
            >
              {sending ? "SENDING..." : "✉ EMAIL TECH-PACK"}
            </button>
            <button
              data-testid="fn-download-btn"
              onClick={downloadPdf}
              disabled={downloading}
              className="btn-raw"
            >
              {downloading ? "..." : "↓ PDF"}
            </button>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="mt-5">
              <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                // SEND HISTORY
              </div>
              <div className="border border-zinc-800 max-h-[120px] overflow-y-auto">
                {history.slice().reverse().map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-2 border-b border-zinc-900 px-3 py-2 text-[11px] font-mono"
                  >
                    <div className="truncate text-zinc-300">
                      <span className="text-[#d4af37]">✓</span> {h.to}
                      {h.factory_name && (
                        <span className="text-zinc-500"> · {h.factory_name}</span>
                      )}
                    </div>
                    <div className="text-zinc-600 shrink-0">
                      {h.sent_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactoryNotesModal;
